/**
 * Derive PLP tag from URL path for browse API.
 * Used by SSR and client to decide which tag to pass to /page/browse.
 */

const JEWELLERY_PREFIXES = ['/jewellery', '/jewellery/'];
const SEARCH_PREFIX = '/search';
const STORES_CATALOG_REGEX = /^\/stores\/[^/]+\/catalog/;

export interface StoreCatalogUrlInput {
  storeName?: string | null;
  cityName?: string | null;
  storeId?: string | number | null;
}

export function isPlpRoute(pathname: string): boolean {
  const path = pathname.split('?')[0];
  // if (GOLD_COIN_PATH_REGEX.test(path)) return false;
  // Strip .html suffix before checking jewellery prefix (e.g. /jewellery.html)
  const pathNoHtml = path.replace(/\.html?$/i, '');
  if (JEWELLERY_PREFIXES.some((p) => pathNoHtml === p || pathNoHtml.startsWith(p + '/'))) return true;
  if (path === SEARCH_PREFIX || path.startsWith(SEARCH_PREFIX + '/')) return true;
  if (STORES_CATALOG_REGEX.test(path) || path.includes('/catalog')) return true;
  return false;
}

export function getPreferredProductsFromSearch(search: string): string | undefined {
  const raw = getRawPreferredProductsFromSearch(search);
  return normalizeQueryParam(raw);
}

export function getRawPreferredProductsFromSearch(search: string): string | undefined {
  const params = getSearchParams(search);
  const raw = params.get('preferredProducts');
  const trimmed = trimQueryParamString(raw);
  return trimmed ? normalizeUriDecoded(trimmed) : undefined;
}

function normalizeQueryParam(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const encoded = normalizeQueryForApi(raw);
  return encoded || undefined;
}

export function normalizeQueryForApi(raw: string): string {
  const normalized = normalizeUriDecoded(raw.trim());
  if (!normalized) return '';
  if (isBase64Encoded(normalized)) return normalized;
  return encodeBase64Universal(normalized);
}

function trimQueryParamString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (Array.isArray(value)) {
    return trimQueryParamString(value[0]);
  }
  return undefined;
}

function isBase64Encoded(value: string): boolean {
  const trimmed = value.replace(/\s/g, '');
  if (!trimmed || trimmed.length % 4 === 1) return false;
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(trimmed)) return false;
  const decoded = decodeBase64Safe(trimmed);
  if (decoded == null) return false;
  return encodeBase64Universal(decoded) === trimmed;
}

function getSearchParams(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith('?') ? search : search ? `?${search}` : '');
}

export function getPreferredProductsFromQuery(query: Record<string, unknown> | undefined): string | undefined {
  if (!query) return undefined;
  const raw = trimQueryParamString(query.preferredProducts);
  return normalizeQueryParam(raw);
}

/** Shared PLP API query context from URL search (storeIds, wid, sid, design_ids, url_query_string). */
export function getPlpApiQueryContextFromSearch(search: string): {
  storeIds?: string;
  wid?: string;
  sid?: string;
  design_ids?: string;
  url_query_string?: string;
  customer_code?: string;
} {
  const normalizedSearch = search.startsWith('?') ? search : search ? `?${search}` : '';
  const params = getSearchParams(normalizedSearch);
  const urlQueryString = normalizedSearch.startsWith('?') ? normalizedSearch.slice(1) : normalizedSearch;

  return {
    ...(trimQueryParamString(params.get('storeIds')) ? { storeIds: trimQueryParamString(params.get('storeIds')) } : {}),
    ...(trimQueryParamString(params.get('wid')) ? { wid: trimQueryParamString(params.get('wid')) } : {}),
    ...(trimQueryParamString(params.get('sid')) ? { sid: trimQueryParamString(params.get('sid')) } : {}),
    ...(trimQueryParamString(params.get('design_ids')) ? { design_ids: trimQueryParamString(params.get('design_ids')) } : {}),
    ...(trimQueryParamString(params.get('customer_code')) ? { customer_code: trimQueryParamString(params.get('customer_code')) } : {}),
    ...(urlQueryString ? { url_query_string: urlQueryString } : {}),
  };
}

/**
 * Stable listing identity (`pathname` + `search`) for SSR seeding and client restore.
 * Accepts a full URL string so SSR can normalize `req` URLs safely before MemoryRouter.
 */
export function getPlpListingKey(pathnameOrUrl: string, search?: string): string {
  const raw =
    search != null && search !== ''
      ? `${pathnameOrUrl}${search.startsWith('?') ? search : `?${search}`}`
      : pathnameOrUrl;
  try {
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      const parsed = new URL(raw);
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // fall through
  }
  const [pathAndQuery] = raw.split('#');
  const qIndex = pathAndQuery.indexOf('?');
  if (qIndex >= 0) {
    const path = pathAndQuery.slice(0, qIndex) || '/';
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedPath}${pathAndQuery.slice(qIndex)}`;
  }
  const normalizedPath = pathAndQuery.startsWith('/') ? pathAndQuery : `/${pathAndQuery}`;
  return normalizedPath;
}

export function getPlpTagFromUrl(pathname: string): string {
  const path = pathname.split('?')[0].replace(/\.html?$/i, '');
  if (path === '/jewellery' || path === '/jewellery/') return 'jewellery';
  const jewelleryMatch = path.match(/^\/jewellery\/(?:filter\/)?(?:[^/]+\/)?([^/]+)/);
  if (jewelleryMatch) {
    const tag = jewelleryMatch[1];
    return tag || 'jewellery';
  }
  if (path.startsWith('/search')) return 'jewellery';
  if (path.includes('/catalog')) {
    const m = path.match(/\/catalog(?:\/filter)?\/([^/]+)/);
    return m ? m[1] : 'jewellery';
  }
  return 'jewellery';
}

/**
 * Browse `/page/browse` `tags` aligned with React Router `:tagString` on PLP routes.
 * Use for SSR and client when `params.tagString` is absent (search / store catalog paths).
 */
export function getPlpBrowseTagsForApi(pathname: string): string {
  const pathOnly = pathname.split('?')[0];
  if (/^\/jewellery(?:\/|\.html)?$/i.test(pathOnly)) {
    return '';
  }

  const path = pathOnly.replace(/\.html?$/i, '');

  const tagPatterns: RegExp[] = [
    /^\/jewellery\/filter\/[^/]+\/([^/]+)$/,
    /^\/jewellery\/filter\/([^/]+)$/,
    /^\/jewellery\/[^/]+\/([^/]+)$/,
    /^\/jewellery\/([^/]+)$/,
    /^\/stores\/[^/]+\/catalog\/filter\/([^/]+)$/,
    /^\/stores\/[^/]+\/catalog\/([^/]+)$/,
  ];

  for (const pattern of tagPatterns) {
    const match = path.match(pattern);
    if (match?.[1]) return match[1];
  }

  // Store catalog base URL with no filter tag (e.g. /stores/<slug>/catalog) — no filter applied.
  if (/^\/stores\/[^/]+\/catalog$/.test(path)) return '';

  return getPlpTagFromUrl(pathname);
}

export function isStoreCatalogRoute(pathname: string): boolean {
  const path = pathname.split('?')[0];
  return STORES_CATALOG_REGEX.test(path);
}

/**
 * Multi-store URLs encode a comma-separated SID list as base64 in the `-reslnk-` token.
 * Detect by decoding that token once and checking whether it contains multiple entries.
 */
export function isMultiStoreCatalogRoute(pathname: string): boolean {
  const path = pathname.split('?')[0];
  const match = path.match(/^\/stores\/([^/]+)\/catalog(?:\.html)?/i);
  if (!match) return false;

  const sidUrlPart = decodeURIComponentSafe(match[1] ?? '');
  const sidToken = sidUrlPart.split('-reslnk-').pop()?.trim();
  if (!sidToken) return false;

  const decoded = decodeBase64Safe(decodeURIComponentSafe(sidToken));
  if (!decoded) return false;

  const sidParts = decoded
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  return sidParts.length > 1;
}

/**
 * Build canonical store PLP URL:
 * /stores/<store-name>-<city-name>-reslnk-<encodedDoubleBase64StoreId>/catalog.html
 */
export function buildStoreCatalogUrl({
  storeName,
  cityName,
  storeId,
}: StoreCatalogUrlInput): string {
  const storeSlug = toKebabCase(firstCommaPart(storeName)) || 'store';
  const citySlug = toKebabCase(firstCommaPart(cityName)) || 'city';
  const sid = encodeStoreSid(storeId);

  return `/stores/${storeSlug}-${citySlug}-reslnk-${sid}/catalog.html`;
}

/**
 * Build store PLP URL for one or more selected stores (legacy PDP nearby-stores flow).
 * Single store: /stores/<store>-<city>-reslnk-<sid>/catalog.html
 * Multiple stores: /stores/<city>-reslnk-<encodedSidList>/catalog.html
 */
export function buildPdpStoresCatalogUrlFromSelection(
  selectedStores: Array<{ storeId: string; storeName: string; cityName: string }>
): string {
  if (selectedStores.length === 0) return '';

  if (selectedStores.length === 1) {
    const store = selectedStores[0];
    return buildPdpStoreCatalogUrl({
      storeName: store.storeName,
      cityName: store.cityName,
      storeId: store.storeId,
    });
  }

  const citySlug = toKebabCase(firstCommaPart(selectedStores[0]?.cityName)) || 'city';
  const sidParam = encodeMultiStoreSidParam(selectedStores.map((store) => store.storeId));
  if (!sidParam) return '';

  return `/stores/${citySlug}-reslnk-${sidParam}/catalog.html`;
}

/**
 * Build canonical store PLP URL for PDP nearby-store navigation.
 * Matches legacy mobile StoresNearYou double-base64 SID encoding.
 */
export function buildPdpStoreCatalogUrl({
  storeName,
  cityName,
  storeId,
}: StoreCatalogUrlInput): string {
  const storeSlug = toKebabCase(firstCommaPart(storeName)) || 'store';
  const citySlug = toKebabCase(firstCommaPart(cityName)) || 'city';
  const sid = encodePdpStoreSid(storeId);

  return `/stores/${storeSlug}-${citySlug}-reslnk-${sid}/catalog.html`;
}

/**
 * Build store-browse API context from store catalog URL.
 * Example:
 * /stores/pacific-mall-new-delhi-reslnk-TVE9PQ%3D%3D/catalog.html
 * => sidUrl: pacific-mall-new-delhi-reslnk-TVE9PQ%3D%3D, sid: TVE9PQ%3D%3D
 */
export function getStoreBrowseContextFromUrl(pathname: string): {
  sid?: string;
  sidUrl?: string;
  sidType?: number;
} {
  const path = pathname.split('?')[0];
  const match = path.match(/^\/stores\/([^/]+)\/catalog(?:\.html)?/i);
  if (!match) return {};

  const rawSidUrl = match[1]?.trim();
  if (!rawSidUrl) return {};

  const decodedRawSidUrl = decodeURIComponentSafe(rawSidUrl);
  const sidUrl = encodeURIComponent(decodedRawSidUrl);

  const hasReslnk = decodedRawSidUrl.includes('-reslnk-');
  const sidRaw = hasReslnk ? decodedRawSidUrl.split('-reslnk-').pop()?.trim() : undefined;
  const sid = sidRaw ? encodeURIComponent(decodeURIComponentSafe(sidRaw)) : undefined;

  return {
    sid,
    sidUrl,
    // New city route: /stores/new-delhi/catalog.html -> sidType=0, sidUrl=new-delhi
    // Old store route: /stores/<store>-<city>-reslnk-<sid>/catalog.html -> sidType=1, sidUrl=<...-reslnk-...>
    sidType: hasReslnk ? 1 : 0,
  };
}

/**
 * Best-effort parsing for displaying the selected store on Store PLP pages.
 * The canonical URL format is:
 * /stores/<store-name>-<city-name>-reslnk-<encodedStoreId>/catalog.html
 *
 * Since both store-name and city-name are kebab-cased and concatenated,
 * we split on "-reslnk-" and then heuristically assign the last 1-2 words to the city.
 */
export function getStoreCatalogDisplayFromUrl(pathname: string): {
  storeName?: string;
  cityName?: string;
} {
  const path = pathname.split('?')[0];
  const match = path.match(/^\/stores\/([^/]+)\/catalog(?:\.html)?/i);
  if (!match) return {};

  const rawSidUrl = match[1]?.trim();
  if (!rawSidUrl) return {};

  const decoded = decodeURIComponentSafe(rawSidUrl);
  const beforeReslnk = decoded.split('-reslnk-')[0]?.trim() ?? '';
  if (!beforeReslnk) return {};

  const displayName = beforeReslnk?.split(",")[0];

  return displayName ? { storeName: displayName, cityName: beforeReslnk?.split(",")[1] } : {};
}

function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function firstCommaPart(value: string | null | undefined): string {
  if (typeof value !== 'string') return '';
  return value.split(',')[0]?.trim() ?? '';
}

function toKebabCase(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

function encodeStoreSid(storeId: string | number | null | undefined): string {
  const source = String(storeId ?? '').trim();
  if (!source) return '';
  // Keep this exactly aligned to legacy flow:
  // 1) btoaUniversal(sid-from-cityToStoresMap)
  // 2) encodeURIComponentOnce(result)
  const normalizedSid = normalizeUriDecoded(source);
  const btoaSid = encodeBase64Universal(normalizedSid);
  return encodeURIComponentOnce(btoaSid);
}

/** Legacy PDP nearby-store SID token: encodeURIComponentOnce(btoa(btoa(storeId))). */
function encodePdpStoreSid(storeId: string | number | null | undefined): string {
  const source = String(storeId ?? '').trim();
  if (!source) return '';
  const normalizedSid = normalizeUriDecoded(source);
  const doubleEncodedSid = encodeBase64Universal(encodeBase64Universal(normalizedSid));
  return encodeURIComponentOnce(doubleEncodedSid);
}

/** Legacy multi-store SID token: btoa(btoa(id1),btoa(id2),...). */
function encodeMultiStoreSidParam(storeIds: string[]): string {
  const normalizedIds = storeIds
    .map((id) => normalizeUriDecoded(String(id).trim()))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  if (normalizedIds.length === 0) return '';

  const joined = normalizedIds.map((id) => encodeBase64Universal(id)).join(',');
  return encodeURIComponentOnce(encodeBase64Universal(joined));
}

function encodeBase64Universal(value: string): string {
  if (typeof globalThis.btoa === 'function') return globalThis.btoa(value);
  // Node SSR fallback
  return Buffer.from(value, 'utf-8').toString('base64');
}

// encodeURIComponent only once (avoid %253D)
function encodeURIComponentOnce(value: string): string {
  return encodeURIComponent(normalizeUriDecoded(value));
}

function normalizeUriDecoded(value: string): string {
  let current = value;
  // Some legacy flows pass SID as double-encoded URI component (%253D).
  // Decode repeatedly until stable so we can re-encode exactly once.
  for (let i = 0; i < 3; i += 1) {
    const next = decodeURIComponentSafe(current);
    if (next === current) break;
    current = next;
  }
  return current;
}

function decodeBase64Safe(value: string): string | null {
  try {
    if (typeof globalThis.atob === 'function') {
      return globalThis.atob(value);
    }
    return Buffer.from(value, 'base64').toString('utf-8');
  } catch {
    return null;
  }
}
